import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { DestinationId, DestinationRunStatus, UploadAnalysis, VendorRouting } from '../lib/types';
import { deriveVendorRouting, validateUpload } from '../lib/csvUpload';
import { Stepper, type StepDef } from './ui/Stepper';
import { Step1Upload } from './steps/Step1Upload';
import { Step2Mapping } from './steps/Step2Mapping';
import { Step3Destinations } from './steps/Step3Destinations';
import { Step4Review } from './steps/Step4Review';
import { ProcessingView } from './views/ProcessingView';
import { ResultsView } from './views/ResultsView';
type Phase = 'wizard' | 'processing' | 'results';
const STEPS: StepDef[] = [{
  id: 1,
  label: 'Upload CSV',
  description: 'Select & validate'
}, {
  id: 2,
  label: 'Map fields',
  description: 'Match columns'
}, {
  id: 3,
  label: 'Destinations',
  description: 'Route leads'
}, {
  id: 4,
  label: 'Review',
  description: 'Confirm & submit'
}];
export function LeadUploader() {
  const [phase, setPhase] = useState<Phase>('wizard');
  const [step, setStep] = useState(1);
  const [analysis, setAnalysis] = useState<UploadAnalysis | null>(null);
  const [destinations, setDestinations] = useState<DestinationId[]>(['salesforce', 'bonzo', 'five9']);
  const [routing, setRouting] = useState<VendorRouting[]>([]);
  const [uploadCount, setUploadCount] = useState(0);
  const [results, setResults] = useState<Record<DestinationId, DestinationRunStatus>>({
    salesforce: 'waiting',
    bonzo: 'waiting',
    five9: 'waiting'
  });
  const validation = useMemo(() => analysis ? validateUpload(analysis.records, analysis.columns) : null, [analysis]);
  const totalRows = analysis?.file.rows ?? 0;
  function setUpload(nextAnalysis: UploadAnalysis) {
    setAnalysis(nextAnalysis);
    setRouting(nextAnalysis.routing);
    setStep(1);
  }
  function updateColumns(columns: UploadAnalysis['columns']) {
    setAnalysis(current => current ? {
      ...current,
      columns
    } : current);
    setRouting(current => analysis ? mergeRouting(deriveVendorRouting(analysis.records, columns), current) : current);
  }
  function toggleDestination(id: DestinationId) {
    setDestinations(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  }
  function reset() {
    setPhase('wizard');
    setStep(1);
    setAnalysis(null);
    setDestinations(['salesforce', 'bonzo', 'five9']);
    setRouting([]);
    setUploadCount(0);
    setResults({
      salesforce: 'waiting',
      bonzo: 'waiting',
      five9: 'waiting'
    });
  }
  return <div className="space-y-6" data-id="element-18" data-mp="wzlj7lahetw9">
    {phase === 'wizard' && <div className="rounded-2xl border border-gray-200 bg-white px-5 py-5 shadow-card sm:px-8" data-id="element-19" data-mp="x1y9qmub2ckx"><Stepper steps={STEPS} current={step} onStepClick={id => setStep(id)} data-id="element-20" data-mp="s7bzwo3vgs2y" /></div>}
    <AnimatePresence mode="wait" data-id="element-21" data-mp="o71rz5wirrlg"><motion.div key={`${phase}-${step}`} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.25
      }} data-id="element-22" data-mp="o5qoz8awenve">
      {phase === 'wizard' && step === 1 && <Step1Upload file={analysis?.file ?? null} summary={validation} onFileSelected={setUpload} onRemove={() => setAnalysis(null)} onContinue={() => setStep(2)} data-id="element-23" data-mp="1fwwgj0cjzd92" />}
      {phase === 'wizard' && step === 2 && analysis && <Step2Mapping columns={analysis.columns} records={analysis.records} onChange={updateColumns} onBack={() => setStep(1)} onContinue={() => {
          setRouting(current => mergeRouting(deriveVendorRouting(analysis.records, analysis.columns), current));
          setStep(3);
        }} data-id="element-24" data-mp="37mfdwxvrankl" />}
      {phase === 'wizard' && step === 3 && analysis && <Step3Destinations selected={destinations} onToggle={toggleDestination} routing={routing} onRoutingChange={setRouting} onBack={() => setStep(2)} onContinue={() => setStep(4)} data-id="element-25" data-mp="3miwqfsfom88q" />}
      {phase === 'wizard' && step === 4 && analysis && validation && <Step4Review selected={destinations} routing={routing} columns={analysis.columns} problems={validation.problems} summary={validation} totalRows={totalRows} onBack={() => setStep(3)} onBackToMapping={() => setStep(2)} onCancel={reset} onSubmit={count => {
          setUploadCount(count);
          setPhase('processing');
        }} data-id="element-26" data-mp="3vayjh5nf8m8z" />}
      {phase === 'processing' && <ProcessingView selected={destinations} totalRecords={uploadCount} onComplete={nextResults => {
          setResults(nextResults);
          setPhase('results');
        }} data-id="element-27" data-mp="3monldc9988lq" />}
      {phase === 'results' && <ResultsView selected={destinations} results={results} totalRecords={uploadCount} onStartAnother={reset} onRetry={id => setResults(current => ({
          ...current,
          [id]: 'completed'
        }))} data-id="element-28" data-mp="8jcfpxytahe" />}
    </motion.div></AnimatePresence>
  </div>;
}
function mergeRouting(generated: VendorRouting[], current: VendorRouting[]): VendorRouting[] {
  return generated.map(route => {
    const prior = current.find(item => item.vendor === route.vendor);
    return prior && !prior.autoSelected ? {
      ...route,
      bonzoCampaignId: prior.bonzoCampaignId,
      five9ListId: prior.five9ListId,
      autoSelected: false
    } : route;
  });
}